export type CalculatorExplainer = {
  title: string;
  summary: string;
  steps: string[];
  reference?: {
    label: string;
    href: string;
  };
};

const explainersByCanonicalPath: Record<string, CalculatorExplainer> = {
  '/': {
    title: 'How this FIRE projection works',
    summary: 'Balances are projected year by year using your contribution cadence and growth assumptions.',
    steps: [
      'Annual or monthly contributions are applied to each account through retirement age.',
      'Portfolio growth is compounded each year using your expected return.',
      'The table and chart show full age-by-age balances for 401(k), Roth, taxable, and total portfolio.'
    ]
  },
  '/advanced-calculator': {
    title: 'How this advanced drawdown model works',
    summary: 'This model tracks inflation-adjusted withdrawal targets and account-level source sequencing.',
    steps: [
      'Before the penalty-free age, withdrawals use taxable first, then Roth, then 401(k) as a last resort because it adds a 10% early-withdrawal penalty.',
      'At and after the penalty-free age, withdrawals use 401(k) up to the configured cap, then taxable, then 401(k) above the cap, then Roth last so tax-free dollars compound longest.',
      'Each year reports target withdrawal, actual withdrawal, source mix, and any shortfall when assets are insufficient.'
    ],
    reference: {
      label: 'Tax-aware withdrawal best-practice discussion (NewRetirement)',
      href: 'https://www.newretirement.com/retirement/retirement-withdrawal-strategies/'
    }
  },
  '/coast-fire-calculator': {
    title: 'How this Coast FIRE model works',
    summary: 'The calculator compares your current savings trajectory with your target retirement requirement.',
    steps: [
      'The tool computes a FIRE target from expenses and withdrawal rate, or uses your direct target value.',
      'Current savings are projected forward at your expected return with inflation adjustment.',
      'The chart compares projected balance versus target line across the full timeline.'
    ]
  },
  '/compound-interest': {
    title: 'How compound growth is calculated',
    summary: 'The model compounds principal and recurring contributions across your selected time horizon.',
    steps: [
      'Each period applies growth based on your selected annual rate and compounding frequency.',
      'Recurring contributions are added and tracked separately from market-driven growth.',
      'The output table separates starting balance, contributions, interest, and ending balance by year.'
    ]
  },
  '/retirement-goal-calculator': {
    title: 'How required savings is solved',
    summary: 'The calculator works backward from your target amount and retirement age to estimate needed monthly savings.',
    steps: [
      'Current savings are projected forward using your expected return assumption.',
      'A required contribution is solved to close any gap to your target by retirement.',
      'Scenario rows stress the monthly requirement under alternative return assumptions.'
    ]
  },
  '/budget-planner': {
    title: 'How budget allocation is summarized',
    summary: 'Income and expense items are grouped into needs, wants, and savings categories.',
    steps: [
      'All income entries are summed to monthly total income.',
      'Expense items are rolled up by category and type tags.',
      'Charts and summary cards show allocation ratios and remaining monthly cash flow.'
    ]
  },
  '/debt-payoff': {
    title: 'How debt payoff strategies are compared',
    summary: 'The calculator simulates minimum, avalanche, and snowball methods using your debt list and extra payment.',
    steps: [
      'Minimum strategy pays only required minimums.',
      'Avalanche directs extra cash to highest APR debt first.',
      'Snowball directs extra cash to smallest balance first and compares payoff time and total interest.'
    ]
  },
  '/emergency-fund': {
    title: 'How emergency fund targets are derived',
    summary: 'The model estimates recommended reserve size from monthly expenses and stability inputs.',
    steps: [
      'Monthly essentials are summed into a baseline spending requirement.',
      'Coverage months are selected from employment, income, and household risk factors.',
      'Timeline output shows projected savings growth against the target reserve line.'
    ]
  },
  '/mortgage-calculator': {
    title: 'How mortgage costs are modeled',
    summary: 'Loan payment and amortization are computed from purchase, rate, term, and escrow assumptions.',
    steps: [
      'Principal and interest are calculated from loan amount, term, and rate.',
      'Taxes, insurance, HOA, PMI, and extra principal are applied to total monthly payment.',
      'Amortization outputs track cumulative principal, interest, and ending balance over time.'
    ]
  },
  '/paycheck-calculator': {
    title: 'How paycheck net pay is estimated',
    summary: 'Gross pay, pre-tax deductions, payroll taxes, and withholding assumptions are combined per pay period.',
    steps: [
      'Salary is converted to pay-period gross using your selected pay frequency.',
      'Pre-tax items like 401(k) and HSA reduce taxable wages before federal and state tax estimates.',
      'The final breakdown shows per-period and annualized take-home versus deductions.'
    ]
  },
  '/rent-vs-buy': {
    title: 'How rent and buy paths are compared',
    summary: 'The model projects total housing cost under both scenarios across your selected horizon.',
    steps: [
      'Rent costs include rent escalation and renters insurance.',
      'Buy costs include financing, taxes, insurance, maintenance, and transaction costs.',
      'Yearly totals are compared to identify break-even timing and lower-cost path under assumptions.'
    ]
  },
  '/job-offer-comparison': {
    title: 'How competing offers are scored',
    summary: 'Each offer is projected year by year with cash, equity vesting, and liquidity events under a shared tax model.',
    steps: [
      'Base pay, sign-on, and bonuses are taxed at the effective cash rate for your filing status and state.',
      'RSU, NSO, and ISO grants vest on schedule; double-trigger RSUs defer until the liquidity event and revalue at FMV.',
      'Tender and IPO windows apply greedy cashless exercise in NSO, held-LTCG, and ISO order with optional QSBS and donation matching.'
    ]
  },
  '/equity-calculator': {
    title: 'How your equity is projected',
    summary: 'Every grant is projected against one shared share-price path, then aggregated into a vested, unvested, and after-tax timeline.',
    steps: [
      'A single price path is built from today\'s price and your expected annual growth, and applied to all holdings.',
      'Each grant vests on its own schedule: RSUs and PSUs at FMV, options and SARs on the spread above strike or baseline, ESPP at the discounted purchase price.',
      'Ordinary tax is applied at vest for full-value grants and capital-gains rules to later appreciation, so vested value and after-tax cash are shown side by side.'
    ]
  }
};

function normalizeCalculatorPath(pathname: string): string {
  if (pathname === '/index.html') {
    return '/';
  }

  if (pathname.endsWith('.html')) {
    return pathname.slice(0, -5);
  }

  return pathname;
}

export function getCalculatorExplainer(pathname: string): CalculatorExplainer | null {
  const canonicalPath = normalizeCalculatorPath(pathname);
  return explainersByCanonicalPath[canonicalPath] ?? null;
}
